import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { row, text } from "@web/theme/theme";

export type RowProps = {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-side content; defaults to a chevron when `onClick` is set. */
  trailing?: ReactNode;
  onClick?: () => void;
  /** Reflect a parent command's synchronous lock on the native row button. */
  disabled?: boolean;
  /** Let title/subtitle wrap instead of truncating to one line. */
  wrap?: boolean;
  /** Quick-action buttons beside the row (e.g. call / text); the row body stays tappable via `onClick`. */
  actions?: ReactNode;
  className?: string;
  variant?: "default" | "record";
  selected?: boolean;
  validationField?: string;
};

/**
 * List row: icon · title/subtitle · trailing. Interactive rows render a <button>
 * (≥44px, focus ring). Rows separate with a hairline; the last has none.
 */
export function Row({ icon, title, subtitle, trailing, onClick, disabled = false, wrap = false, actions, className = "", variant = "default", selected = false, validationField }: RowProps) {
  if (variant === "record") className = `v365-record-row ${className}`;
  if (selected) className = `v365-row-selected ${className}`;
  const classes = `${row.base} ${actions ? row.main : row.divider} ${onClick ? row.interactive : ""} ${actions ? "" : className}`.trim();
  const clamp = wrap ? "break-words [overflow-wrap:anywhere]" : "truncate";
  const body = (
    <>
      {icon && <span className={row.icon}>{icon}</span>}
      <span className="flex-1 min-w-0 overflow-hidden flex flex-col">
        <span className={`v365-row-title ${text.variant.body} ${text.tone.default} ${clamp}`}>{title}</span>
        {subtitle && <span className={`v365-row-subtitle ${text.variant.callout} ${text.tone.secondary} ${clamp}`}>{subtitle}</span>}
      </span>
      {(trailing ?? (onClick ? <ChevronRight aria-hidden size={18} /> : null)) && (
        <span className={row.trailing}>{trailing ?? <ChevronRight aria-hidden size={18} />}</span>
      )}
    </>
  );
  const main = onClick ? (
    <button type="button" onClick={onClick} disabled={disabled} className={classes} data-validation-field={validationField}>
      {body}
    </button>
  ) : (
    <div className={classes} data-validation-field={validationField} tabIndex={validationField ? -1 : undefined}>{body}</div>
  );
  if (!actions) return main;
  return (
    <div className={`${row.split} ${row.divider} ${className}`.trim()}>
      {main}
      <span className={row.actions}>{actions}</span>
    </div>
  );
}

/** Groups rows into one rounded surface (an iOS "inset grouped" list). */
export function RowGroup({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`v365-row-group flex flex-col rounded-card overflow-hidden bg-surface shadow-card ${className}`.trim()}>{children}</div>;
}
